import React from "react";
import { Button } from 'primereact/button';
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toolbar } from "primereact/toolbar";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DialogBase } from "./DialogBase";
import { Autocomplete, GoogleMap, LoadScript, Marker, InfoWindow } from "@react-google-maps/api";
import { getEnvVar } from "../../config/RuntimeConfig";

interface Props {
    ele: any;
    isEng: boolean;
    dialogVisible: boolean;
    callback: (visible?: boolean) => void;
}

interface MapLocation {
    id: string;
    name: string;
    nameFRA: string;
    address: string;
    lat: number;
    lng: number;
    description?: string;
    descriptionFRA?: string;
}

interface State {
    locations: MapLocation[];
    selectedLocation: MapLocation | null;
    showAddLocationDialog: boolean;
    showConfirmDelete: boolean;
    locationToDelete: MapLocation | null;
    selectedPreviewMarker: MapLocation | null;
    newLocation: {
        name: string;
        nameFRA: string;
        description: string;
        descriptionFRA: string;
        address: string;
        lat: number;
        lng: number;
    };
    mapCenter: { lat: number; lng: number };
    showEditLocationDialog: boolean;
    editingLocation: MapLocation | null;
}

const containerStyle = {
    width: "100%",
    height: "300px",
};

const defaultCenter = {
    lat: 43.6532, // Toronto
    lng: -79.3832,
};

const GOOGLE_MAPS_API_KEY = getEnvVar('GOOGLE_MAPS_API_KEY') || "";

const AUTOCOMPLETE_OPTIONS = {
    types: ['establishment', 'geocode'],
    componentRestrictions: { country: ['ca'] },
    fields: ['formatted_address', 'geometry', 'name', 'place_id'],
};

export class DialogForGoogleMapComponent extends DialogBase<Props, State> {
    private autocomplete: google.maps.places.Autocomplete | null = null;

    constructor(props: Props) {
        super(props);

        const existingLocations = (this.props.ele as any).mapLocations || [];

        this.state = {
            locations: existingLocations,
            selectedLocation: null,
            showAddLocationDialog: false,
            showConfirmDelete: false,
            locationToDelete: null,
            selectedPreviewMarker: null,
            newLocation: {
                name: '',
                nameFRA: '',
                description: '',
                descriptionFRA: '',
                address: '',
                lat: defaultCenter.lat,
                lng: defaultCenter.lng,
            },
            mapCenter: this.getMapCenter(existingLocations),
            showEditLocationDialog: false,
            editingLocation: null,
        };
    }

    getMapCenter = (locations: MapLocation[] = this.state?.locations || []) => {
        if (locations.length === 0) return defaultCenter;
        
        if (locations.length === 1) {
            return { lat: locations[0].lat, lng: locations[0].lng };
        }
        
        const avgLat = locations.reduce((sum, loc) => sum + loc.lat, 0) / locations.length;
        const avgLng = locations.reduce((sum, loc) => sum + loc.lng, 0) / locations.length;
        
        return { lat: avgLat, lng: avgLng };
    };

    handleAddLocation = () => {
        this.setState({
            showAddLocationDialog: true,
            newLocation: {
                name: '',
                nameFRA: '',
                description: '',
                descriptionFRA: '',
                address: '',
                lat: defaultCenter.lat,
                lng: defaultCenter.lng,
            }
        });
    };

    handleSaveLocation = () => {
        const { newLocation, locations } = this.state;

        if (this.props.isEng) {
            if (!newLocation.name.trim()) {
                alert('Location name is required');
                return;
            }
        } else {
            if (!newLocation.nameFRA.trim()) {
                alert('Le nom de l\'emplacement est obligatoire');
                return;
            }
        }

        if (!newLocation.address.trim()) {
            alert(this.props.isEng ? 'Address is required' : 'L\'adresse est obligatoire');
            return;
        }

        const location: MapLocation = {
            id: Date.now().toString(),
            name: newLocation.name.trim(),
            nameFRA: newLocation.nameFRA.trim(),
            address: newLocation.address.trim(),
            description: newLocation.description.trim(),
            descriptionFRA: newLocation.descriptionFRA.trim(),
            lat: newLocation.lat,
            lng: newLocation.lng,
        };

        const updatedLocations = [...locations, location];

        this.setState({
            locations: updatedLocations,
            showAddLocationDialog: false,
            mapCenter: this.getMapCenter(updatedLocations),
        });

        (this.props.ele as any).mapLocations = updatedLocations;
        this.props.callback();
    };

    handleEditLocation = (location: MapLocation) => {
        this.setState({
            showEditLocationDialog: true,
            editingLocation: location,
            newLocation: {
                name: location.name,
                nameFRA: location.nameFRA || '',
                description: location.description || '',
                descriptionFRA: location.descriptionFRA || '',
                address: location.address,
                lat: location.lat,
                lng: location.lng,
            },
            mapCenter: { lat: location.lat, lng: location.lng },
        });
    };

    handleUpdateLocation = () => {
        const { newLocation, locations, editingLocation } = this.state;

        if (!editingLocation) return;

        if (this.props.isEng) {
            if (!newLocation.name.trim()) {
                alert('Location name is required');
                return;
            }
        } else {
            if (!newLocation.nameFRA.trim()) {
                alert('Le nom de l\'emplacement est obligatoire');
                return;
            }
        }

        if (!newLocation.address.trim()) {
            alert(this.props.isEng ? 'Address is required' : 'L\'adresse est obligatoire');
            return;
        }

        const updated: MapLocation = {
            id: editingLocation.id,
            name: newLocation.name.trim(),
            nameFRA: newLocation.nameFRA.trim(),
            address: newLocation.address.trim(),
            description: newLocation.description.trim(),
            descriptionFRA: newLocation.descriptionFRA.trim(),
            lat: newLocation.lat,
            lng: newLocation.lng,
        };

        const updatedLocations = locations.map(l => (l.id === editingLocation.id ? updated : l));

        this.setState({
            locations: updatedLocations,
            showEditLocationDialog: false,
            editingLocation: null,
            mapCenter: this.getMapCenter(updatedLocations),
        });

        (this.props.ele as any).mapLocations = updatedLocations;
        this.props.callback();
    };

    handleDeleteLocation = (location: MapLocation) => {
        this.setState({
            showConfirmDelete: true,
            locationToDelete: location,
        });
    };

    confirmDelete = () => {
        const { locations, locationToDelete } = this.state;

        if (locationToDelete) {
            const updatedLocations = locations.filter(loc => loc.id !== locationToDelete.id);

            this.setState({
                locations: updatedLocations,
                showConfirmDelete: false,
                locationToDelete: null,
                mapCenter: this.getMapCenter(updatedLocations),
            });

            (this.props.ele as any).mapLocations = updatedLocations;
            this.props.callback();
        }
    };

    handleMapClick = (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();

            this.setState({
                newLocation: {
                    ...this.state.newLocation,
                    lat,
                    lng,
                }
            });
        }
    };

    onAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
        this.autocomplete = autocomplete;
    };

    onPlaceChanged = () => {
        if (this.autocomplete) {
            const place = this.autocomplete.getPlace();
            
            if (place.geometry && place.geometry.location) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                const address = place.formatted_address || '';

                this.setState({
                    newLocation: {
                        ...this.state.newLocation,
                        address,
                        lat,
                        lng,
                    },
                    mapCenter: { lat, lng }
                });
            }
        }
    };

    handlePreviewMarkerClick = (location: MapLocation) => {
        this.setState({ selectedPreviewMarker: location });
    };

    renderLocationActions = (location: MapLocation) => {
        return (
            <div>
                <Button
                    icon="pi pi-eye"
                    className="p-button-text p-button-sm"
                    onClick={() => {
                        this.setState({
                            mapCenter: { lat: location.lat, lng: location.lng },
                            selectedPreviewMarker: location
                        });
                    }}
                    tooltip={this.props.isEng ? "View on map" : "Voir sur la carte"}
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-text p-button-sm p-button-danger"
                    onClick={() => this.handleDeleteLocation(location)}
                    tooltip={this.props.isEng ? "Delete location" : "Supprimer l'emplacement"}
                />
            </div>
        );
    };

    renderToolbar = () => {
        return (
            <div className="flex justify-content-between">
                <Button
                    label={this.props.isEng ? "Add Location" : "Ajouter un emplacement"}
                    icon="pi pi-plus"
                    className="p-button-success p-button-sm"
                    onClick={this.handleAddLocation}
                />
            </div>
        );
    };

    renderAddLocationDialog = () => {
        const { showAddLocationDialog, newLocation } = this.state;

        return (
            <Dialog
                header={this.props.isEng ? "Add New Location" : "Ajouter un nouvel emplacement"}
                visible={showAddLocationDialog}
                style={{ width: '90vw', maxWidth: '1200px' }}
                onHide={() => this.setState({ showAddLocationDialog: false })}
                footer={
                    <div>
                        <Button 
                            label={this.props.isEng ? "Cancel" : "Annuler"}
                            icon="pi pi-times" 
                            className="p-button-text"
                            onClick={() => this.setState({ showAddLocationDialog: false })}
                        />
                        <Button 
                            label={this.props.isEng ? "Save" : "Enregistrer"}
                            icon="pi pi-check" 
                            onClick={this.handleSaveLocation}
                        />
                    </div>
                }
            >
                <div className="grid">
                    <div className="col-12 md:col-6">
                        {this.props.isEng ? (
                            <>
                                <div className="field">
                                    <label htmlFor="locationName">
                                        Location Name *
                                    </label>
                                    <InputText
                                        id="locationName"
                                        value={newLocation.name}
                                        onChange={(e) => this.setState({ 
                                            newLocation: { ...newLocation, name: e.target.value } 
                                        })}
                                        className="w-full"
                                        placeholder="Enter location name"
                                    />
                                </div>

                                <div className="field">
                                    <label>
                                        Address *
                                    </label>
                                    <Autocomplete
                                        onLoad={this.onAutocompleteLoad}
                                        onPlaceChanged={this.onPlaceChanged}
                                        options={AUTOCOMPLETE_OPTIONS}
                                    >
                                        <InputText
                                            value={newLocation.address}
                                            onChange={(e) => this.setState({ 
                                                newLocation: { ...newLocation, address: e.target.value } 
                                            })}
                                            className="w-full"
                                            placeholder="Search address"
                                        />
                                    </Autocomplete>
                                </div>

                                <div className="field">
                                    <label htmlFor="locationDescription">
                                        Description
                                    </label>
                                    <InputText
                                        id="locationDescription"
                                        value={newLocation.description}
                                        onChange={(e) => this.setState({ 
                                            newLocation: { ...newLocation, description: e.target.value } 
                                        })}
                                        className="w-full"
                                        placeholder="Optional description"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="field">
                                    <label htmlFor="locationNameFRA">
                                        Nom de l'emplacement *
                                    </label>
                                    <InputText
                                        id="locationNameFRA"
                                        value={newLocation.nameFRA}
                                        onChange={(e) => this.setState({ 
                                            newLocation: { ...newLocation, nameFRA: e.target.value } 
                                        })}
                                        className="w-full"
                                        placeholder="Entrez le nom de l'emplacement"
                                    />
                                </div>

                                <div className="field">
                                    <label>
                                        Adresse *
                                    </label>
                                    <Autocomplete
                                        onLoad={this.onAutocompleteLoad}
                                        onPlaceChanged={this.onPlaceChanged}
                                        options={AUTOCOMPLETE_OPTIONS}
                                    >
                                        <InputText
                                            value={newLocation.address}
                                            onChange={(e) => this.setState({ 
                                                newLocation: { ...newLocation, address: e.target.value } 
                                            })}
                                            className="w-full"
                                            placeholder="Rechercher une adresse"
                                        />
                                    </Autocomplete>
                                </div>

                                <div className="field">
                                    <label htmlFor="locationDescriptionFRA">
                                        Description
                                    </label>
                                    <InputText
                                        id="locationDescriptionFRA"
                                        value={newLocation.descriptionFRA}
                                        onChange={(e) => this.setState({ 
                                            newLocation: { ...newLocation, descriptionFRA: e.target.value } 
                                        })}
                                        className="w-full"
                                        placeholder="Description optionnelle"
                                    />
                                </div>
                            </>
                        )}

                        <div className="field">
                            <small>
                                {this.props.isEng 
                                    ? "Current coordinates:"
                                    : "Coordonnées actuelles :"
                                } {newLocation.lat.toFixed(6)}, {newLocation.lng.toFixed(6)}
                                <br />
                                {this.props.isEng 
                                    ? "Click on the map to adjust exact coordinates"
                                    : "Cliquez sur la carte pour ajuster les coordonnées exactes"
                                }
                            </small>
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={this.state.mapCenter}
                            zoom={12}
                            onClick={this.handleMapClick}
                        >
                            <Marker
                                position={{ lat: newLocation.lat, lng: newLocation.lng }}
                                draggable={true}
                                onDragEnd={(e) => {
                                    if (e.latLng) {
                                        this.setState({
                                            newLocation: {
                                                ...newLocation,
                                                lat: e.latLng.lat(),
                                                lng: e.latLng.lng(),
                                            }
                                        });
                                    }
                                }}
                            />
                        </GoogleMap>
                    </div>
                </div>
            </Dialog>
        );
    };

    renderEditLocationDialog = () => {
        const { showEditLocationDialog, newLocation } = this.state;

        return (
            <Dialog
                header={this.props.isEng ? "Edit Location" : "Modifier l'emplacement"}
                visible={showEditLocationDialog}
                style={{ width: '90vw', maxWidth: '1200px' }}
                onHide={() => this.setState({ showEditLocationDialog: false, editingLocation: null })}
                footer={
                    <div>
                        <Button
                            label={this.props.isEng ? "Cancel" : "Annuler"}
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => this.setState({ showEditLocationDialog: false, editingLocation: null })}
                        />
                        <Button
                            label={this.props.isEng ? "Update" : "Mettre à jour"}
                            icon="pi pi-check"
                            onClick={this.handleUpdateLocation}
                        />
                    </div>
                }
            >
                <div className="grid">
                    <div className="col-12 md:col-6">
                        {this.props.isEng ? (
                            <>
                                <div className="field">
                                    <label htmlFor="editLocationName">
                                        Location Name *
                                    </label>
                                    <InputText
                                        id="editLocationName"
                                        value={newLocation.name}
                                        onChange={(e) => this.setState({
                                            newLocation: { ...newLocation, name: e.target.value }
                                        })}
                                        className="w-full"
                                        placeholder="Enter location name"
                                    />
                                </div>

                                <div className="field">
                                    <label>
                                        Address *
                                    </label>
                                    <Autocomplete
                                        onLoad={this.onAutocompleteLoad}
                                        onPlaceChanged={this.onPlaceChanged}
                                        options={AUTOCOMPLETE_OPTIONS}
                                    >
                                        <InputText
                                            value={newLocation.address}
                                            onChange={(e) => this.setState({
                                                newLocation: { ...newLocation, address: e.target.value }
                                            })}
                                            className="w-full"
                                            placeholder="Search address"
                                        />
                                    </Autocomplete>
                                </div>

                                <div className="field">
                                    <label htmlFor="editLocationDescription">
                                        Description
                                    </label>
                                    <InputText
                                        id="editLocationDescription"
                                        value={newLocation.description}
                                        onChange={(e) => this.setState({
                                            newLocation: { ...newLocation, description: e.target.value }
                                        })}
                                        className="w-full"
                                        placeholder="Optional description"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="field">
                                    <label htmlFor="editLocationNameFRA">
                                        Nom de l'emplacement *
                                    </label>
                                    <InputText
                                        id="editLocationNameFRA"
                                        value={newLocation.nameFRA}
                                        onChange={(e) => this.setState({
                                            newLocation: { ...newLocation, nameFRA: e.target.value }
                                        })}
                                        className="w-full"
                                        placeholder="Entrez le nom de l'emplacement"
                                    />
                                </div>

                                <div className="field">
                                    <label>
                                        Adresse *
                                    </label>
                                    <Autocomplete
                                        onLoad={this.onAutocompleteLoad}
                                        onPlaceChanged={this.onPlaceChanged}
                                        options={AUTOCOMPLETE_OPTIONS}
                                    >
                                        <InputText
                                            value={newLocation.address}
                                            onChange={(e) => this.setState({
                                                newLocation: { ...newLocation, address: e.target.value }
                                            })}
                                            className="w-full"
                                            placeholder="Rechercher une adresse"
                                        />
                                    </Autocomplete>
                                </div>

                                <div className="field">
                                    <label htmlFor="editLocationDescriptionFRA">
                                        Description
                                    </label>
                                    <InputText
                                        id="editLocationDescriptionFRA"
                                        value={newLocation.descriptionFRA}
                                        onChange={(e) => this.setState({
                                            newLocation: { ...newLocation, descriptionFRA: e.target.value }
                                        })}
                                        className="w-full"
                                        placeholder="Description optionnelle"
                                    />
                                </div>
                            </>
                        )}

                        <div className="field">
                            <small>
                                {this.props.isEng 
                                    ? "Current coordinates:"
                                    : "Coordonnées actuelles :"
                                } {newLocation.lat.toFixed(6)}, {newLocation.lng.toFixed(6)}
                                <br />
                                {this.props.isEng 
                                    ? "Click on the map to adjust exact coordinates"
                                    : "Cliquez sur la carte pour ajuster les coordonnées exactes"
                                }
                            </small>
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={this.state.mapCenter}
                            zoom={12}
                            onClick={this.handleMapClick}
                        >
                            <Marker
                                position={{ lat: newLocation.lat, lng: newLocation.lng }}
                                draggable={true}
                                onDragEnd={(e) => {
                                    if (e.latLng) {
                                        this.setState({
                                            newLocation: {
                                                ...newLocation,
                                                lat: e.latLng.lat(),
                                                lng: e.latLng.lng(),
                                            }
                                        });
                                    }
                                }}
                            />
                        </GoogleMap>
                    </div>
                </div>
            </Dialog>
        );
    };

    renderFooter = () => {
        return (
            <div>
                <Button
                    label={this.props.isEng ? "Cancel" : "Annuler"}
                    icon="pi pi-times"
                    className="p-button-text"
                    onClick={() => this.props.callback(false)}
                />
                <Button
                    label="OK"
                    icon="pi pi-check"
                    onClick={() => this.props.callback(false)}
                />
            </div>
        );
    };

    render() {
        const { locations, showConfirmDelete, locationToDelete, selectedPreviewMarker } = this.state;

        return (
            <>
                <LoadScript 
                    id="google-map-script" 
                    googleMapsApiKey={GOOGLE_MAPS_API_KEY} 
                    libraries={['places']}
                >
                    <Dialog
                        header={this.props.isEng ? "Google Map Configuration" : "Configuration de Google Map"}
                        footer={this.renderFooter()}
                        visible={this.props.dialogVisible}
                        style={{ width: '90vw', maxWidth: '1200px' }}
                        onHide={() => this.props.callback(false)}
                    >
                        <div className="grid">
                            <div className="col-12">
                                <Toolbar className="mb-4" left={this.renderToolbar} />
                                <DataTable
                                    value={locations}
                                    selection={this.state.selectedLocation}
                                    onSelectionChange={(e) => this.setState({ selectedLocation: e.value })}
                                    dataKey="id"
                                    rows={10}
                                    emptyMessage={this.props.isEng ? "No locations added yet" : "Aucun emplacement ajouté"}
                                    className="p-datatable-sm"
                                    rowHover
                                    onRowClick={(e: any) => this.handleEditLocation(e.data)}
                                >
                                    <Column 
                                        field={this.props.isEng ? "name" : "nameFRA"} 
                                        header={this.props.isEng ? "Name" : "Nom"} 
                                        sortable 
                                    />
                                    <Column field="address" header={this.props.isEng ? "Address" : "Adresse"} sortable style={{ maxWidth: '300px' }} />
                                    <Column 
                                        field={this.props.isEng ? "description" : "descriptionFRA"} 
                                        header={this.props.isEng ? "Description" : "Description"} 
                                    />
                                    <Column field="lat" header={this.props.isEng ? "Latitude" : "Latitude"} body={(l) => l.lat.toFixed(6)} />
                                    <Column field="lng" header={this.props.isEng ? "Longitude" : "Longitude"} body={(l) => l.lng.toFixed(6)} />
                                    <Column header={this.props.isEng ? "Actions" : "Actions"} body={this.renderLocationActions} style={{ width: '120px' }} />
                                </DataTable>
                            </div>

                            {locations.length > 0 && (
                                <div className="col-12 mt-4">
                                    <h5>{this.props.isEng ? "Map Preview" : "Aperçu de la carte"}</h5>
                                    <GoogleMap
                                        mapContainerStyle={containerStyle}
                                        center={this.getMapCenter(locations)}
                                        zoom={locations.length <= 1 ? 12 : 10}
                                    >
                                        {locations.map((location) => (
                                            <Marker
                                                key={location.id}
                                                position={{ lat: location.lat, lng: location.lng }}
                                                onClick={() => this.handlePreviewMarkerClick(location)}
                                                title={this.props.isEng ? location.name : location.nameFRA}
                                            />
                                        ))}
                                        
                                        {selectedPreviewMarker && (
                                            <InfoWindow
                                                position={{ lat: selectedPreviewMarker.lat, lng: selectedPreviewMarker.lng }}
                                                onCloseClick={() => this.setState({ selectedPreviewMarker: null })}
                                            >
                                                <div style={{ maxWidth: '200px' }}>
                                                    <h6 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
                                                        {this.props.isEng ? selectedPreviewMarker.name : selectedPreviewMarker.nameFRA}
                                                    </h6>
                                                    <p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>
                                                        {selectedPreviewMarker.address}
                                                    </p>
                                                    {((this.props.isEng && selectedPreviewMarker.description) || 
                                                      (!this.props.isEng && selectedPreviewMarker.descriptionFRA)) && (
                                                        <p style={{ margin: '0', fontSize: '11px', fontStyle: 'italic' }}>
                                                            {this.props.isEng ? selectedPreviewMarker.description : selectedPreviewMarker.descriptionFRA}
                                                        </p>
                                                    )}
                                                </div>
                                            </InfoWindow>
                                        )}
                                    </GoogleMap>
                                </div>
                            )}
                        </div>
                    </Dialog>

                    {this.renderAddLocationDialog()}
                    {this.renderEditLocationDialog()} 
                </LoadScript>

                <ConfirmDialog
                    visible={showConfirmDelete}
                    onHide={() => this.setState({ showConfirmDelete: false })}
                    message={this.props.isEng 
                        ? `Are you sure you want to delete "${locationToDelete?.name}"?`
                        : `Êtes-vous sûr de vouloir supprimer "${locationToDelete?.name}" ?`
                    }
                    header={this.props.isEng ? "Confirm Deletion" : "Confirmer la suppression"}
                    icon="pi pi-exclamation-triangle"
                    accept={this.confirmDelete}
                    reject={() => this.setState({ showConfirmDelete: false })}
                />
            </>
        );
    }
}